import type { FeatureCollection, MultiPolygon, Polygon } from "geojson";
import type { BuildingProperties } from "../lib/building";

export interface FloodTerrain {
  source: string;
  acquired: string;
  terrainEpoch: string;
  resolutionM: number;
  nativeResolutionM: number;
  west: number;
  north: number;
  step: number;
  width: number;
  height: number;
  reference: string;
  method: string;
  elevationCm: Array<number | null>;
  referenceCm: Array<number | null>;
  activationCm: Array<number | null>;
  riverCells: number[];
}
export interface FloodCell {
  polygon: [number, number][];
  depthM: number;
}
/** Area-weighted geographic centroid, subtracting holes and weighting parts. */
export function buildingCentroid(geometry: Polygon | MultiPolygon): [number, number] | null {
  const parts = geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;
  let weight = 0, lng = 0, lat = 0;
  for (const rings of parts) for (let r = 0; r < rings.length; r++) {
    const ring = rings[r], origin = ring[0];
    let area = 0, x = 0, y = 0;
    for (let i = 0; i < ring.length - 1; i++) {
      const a = [ring[i][0] - origin[0], ring[i][1] - origin[1]];
      const b = [ring[i + 1][0] - origin[0], ring[i + 1][1] - origin[1]];
      const cross = a[0] * b[1] - b[0] * a[1];
      area += cross; x += (a[0] + b[0]) * cross; y += (a[1] + b[1]) * cross;
    }
    if (Math.abs(area) < 1e-16) continue;
    const w = Math.abs(area) * (r === 0 ? 1 : -1);
    lng += (origin[0] + x / (3 * area)) * w;
    lat += (origin[1] + y / (3 * area)) * w;
    weight += w;
  }
  return weight > 0 ? [lng / weight, lat / weight] : null;
}
export function terrainCell(t: FloodTerrain, lng: number, lat: number): number | null {
  const col = Math.floor((lng - t.west) / t.step), row = Math.floor((t.north - lat) / t.step);
  return col < 0 || row < 0 || col >= t.width || row >= t.height ? null : row * t.width + col;
}
/** A planning footprint count, explicitly NOT households or a flood forecast. */
export function screenFlood(
  terrain: FloodTerrain,
  riseM: number,
  buildings: FeatureCollection<Polygon | MultiPolygon, BuildingProperties> | null,
) {
  const river = new Set(terrain.riverCells), wet = new Set<number>(), cells: FloodCell[] = [];
  const riseCm = Math.max(0, riseM) * 100;
  for (let i = 0; i < terrain.activationCm.length; i++) {
    const activation = terrain.activationCm[i], elevation = terrain.elevationCm[i], reference = terrain.referenceCm[i];
    if (activation == null || elevation == null || reference == null || activation > riseCm || river.has(i)) continue;
    const depthM = (reference + riseCm - elevation) / 100;
    if (depthM <= 0) continue;
    const col = i % terrain.width, row = Math.floor(i / terrain.width);
    const x = terrain.west + col * terrain.step, y = terrain.north - row * terrain.step;
    wet.add(i);
    cells.push({ depthM, polygon: [[x, y], [x + terrain.step, y], [x + terrain.step, y - terrain.step], [x, y - terrain.step]] });
  }
  const exposedIds: string[] = [];
  let unknownBuildings = 0;
  for (const f of buildings?.features ?? []) {
    const center = buildingCentroid(f.geometry);
    const cell = center ? terrainCell(terrain, ...center) : null;
    if (cell == null || terrain.elevationCm[cell] == null) unknownBuildings++;
    else if (wet.has(cell)) exposedIds.push(f.properties.id);
  }
  return { cells, exposedIds, unknownBuildings };
}
